/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useContext } from 'react';
import Bookmark from 'components/Page/Toolbox/Bookmark';
import HyperLink from 'components/Core/HyperLink';
import { useHeaderHeight } from 'hooks/useHeaderHeight';
import HeaderNavContext from 'utils/HeaderNavContext';
import rightArrow from 'images/right-arrow-gray.svg';
import rightArrowLight from 'images/right-arrow-lightgray.svg';
import forwardSlash from 'images/forward_slash.svg';
import forwardSlashWhite from 'images/forward_slash_white.svg';
import { Helmet } from 'react-helmet';
import { generateBreadcrumbSchema } from 'utils/seo';
import { Container, InnerContainer, ChildLink, ParentLink } from './styles';

const Breadcrumbs = ({
  breadcrumbs: initialBreadcrumbs,
  absoluteMode,
  stickyWithoutHeader,
  theme,
  layout,
  ignoreBookmark,
}) => {
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: generateBreadcrumbSchema(initialBreadcrumbs),
  };

  const darkTheme = theme === 'dark';
  const headerHeight = useHeaderHeight();
  const filteredBreadcrumbs = initialBreadcrumbs?.filter((x) => x !== false);
  const { showSimpleOrMain } = useContext(HeaderNavContext);

  useEffect(() => {
    function checkMobileLastBreadcrumb() {
      const children = document.getElementsByClassName('breadcrumb--item-mobile');
      if (children.length <= 2) return;

      const firstChild = children[0];
      const lastChild = children[children.length - 1];
      const secondLastChild = children[children.length - 2];

      if (firstChild.offsetTop !== secondLastChild.offsetTop) {
        lastChild.style.flexBasis = 'auto';
      } else {
        lastChild.style.flexBasis = '100%';
      }
    }

    setTimeout(checkMobileLastBreadcrumb, 100);
    window.addEventListener('resize', checkMobileLastBreadcrumb);
    return () => window.removeEventListener('resize', checkMobileLastBreadcrumb);
  }, []);

  return (
    <>
      {breadcrumbSchema && (
        <Helmet>
          <script type="application/ld+raw">{JSON.stringify(breadcrumbSchema)}</script>
        </Helmet>
      )}
      <Container
        className="breadcrumb--wrapper"
        absoluteMode={absoluteMode}
        simpleHeader={showSimpleOrMain}
        stickyWithoutHeader={stickyWithoutHeader}
        headerHeight={headerHeight}
        layout={layout}
      >
        {filteredBreadcrumbs && (
          <InnerContainer layout={layout} className="breadcrumb--inner-container">
            <DesktopBreadcrumbs breadcrums={filteredBreadcrumbs} darkTheme={darkTheme} />
            <MobileBreadcrumbs breadcrums={filteredBreadcrumbs} darkTheme={darkTheme} />
            {!ignoreBookmark && layout === 'toolbox' && (
              <Bookmark
                label="Bookmark"
                subType="breadcrumb"
                color={theme === 'dark' ? 'var(--grey-8)' : 'var(--white)'}
              />
            )}
          </InnerContainer>
        )}
      </Container>
    </>
  );
};

const formatBreadcrumb = (breadcrumbs, breadcrumb, index, darkTheme) => {
  const singleParentPage = breadcrumbs.length <= 1;

  const hasSubPage =
    !singleParentPage && breadcrumbs.length > index + 1 && index !== breadcrumbs.length - 1;

  if (breadcrumb.url) {
    return (
      <HyperLink href={breadcrumb.url} key={`breadcrumb-${index}`}>
        <ParentLink darkTheme={darkTheme}>
          {singleParentPage && (
            <img
              src={darkTheme ? rightArrow : rightArrowLight}
              className="breadcrumb--arrow reverse"
              alt="breadcrumb-arrow"
            />
          )}
          {breadcrumb.title}
          {breadcrumb.icon && (
            <img src={breadcrumb.icon} className="breadcrumb--icon" alt="breadcrumb icon" />
          )}
          {hasSubPage && (
            <img
              src={darkTheme ? forwardSlash : forwardSlashWhite}
              className="breadcrumb--arrow"
              alt="breadcrumb-arrow"
            />
          )}
        </ParentLink>
      </HyperLink>
    );
  }

  return (
    <ChildLink key={`breadcrumb-${index}`} darkTheme={darkTheme}>
      {breadcrumb.title}
      {breadcrumb.icon && (
        <img src={breadcrumb.icon} className="breadcrumb--icon" alt="breadcrumb icon" />
      )}
    </ChildLink>
  );
};

const DesktopBreadcrumbs = ({ breadcrums, darkTheme }) => (
  <div className="min-md flex flex-row">
    {breadcrums.map((crumb, index) => {
      return formatBreadcrumb(breadcrums, crumb, index, darkTheme);
    })}
  </div>
);

const MobileBreadcrumbs = ({ breadcrums, darkTheme }) => {
  const mobileBreadCrumbs = breadcrums.filter((x) => !x.hideInMobile);
  return (
    <div className="max-md flex flex-row flex-wrap gap-x-0 gap-y-[10px] mt-[13px] pr-[35px]">
      {mobileBreadCrumbs.map((crumb, index) => (
        <div className="breadcrumb--item-mobile">
          {formatBreadcrumb(mobileBreadCrumbs, crumb, index, darkTheme)}
        </div>
      ))}
    </div>
  );
};

export default Breadcrumbs;
